import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Search, Filter, BookOpen, Building2, Users, UserCircle, Briefcase, Truck,
  Sparkles, ChevronRight, Eye, Edit3, Trash2, Plus, Download, ArrowUpDown,
  DollarSign, Phone, Mail, MapPin, Hash, Calendar, X, RefreshCw, CheckCircle,
  ChevronDown, ChevronUp, FolderOpen, FileText, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import PartyFields from '../components/PartyFields';
import PageHeader, { headerButtonClass, headerPrimaryClass } from '../components/layout/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ViewPage = () => {
  const location = useLocation();
  const path = location.pathname;

  const getContent = () => {
    if (path.includes('/view/chart-of-accounts')) return <ChartOfAccounts />;
    if (path.includes('/view/vendors')) return <VendorsList />;
    if (path.includes('/view/customers')) return <CustomersList />;
    if (path.includes('/view/employees')) return <EmployeesList />;
    if (path.includes('/view/enterprises')) return <EnterprisesList />;
    if (path.includes('/view/equipment')) return <EquipmentList />;
    if (path.includes('/view/transactions')) return <TransactionBrowser />;
    if (path.includes('/view/smart-lookup')) return <SmartLookup />;
    return <ViewOverview />;
  };

  return <div className="space-y-6">{getContent()}</div>;
};

// Overview
const ViewOverview = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchStats();
  }, [companyId]);

  const fetchStats = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/system/master-counts${params}`, { credentials: 'include' });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const masterLists = [
    { icon: BookOpen, label: 'Chart of Accounts', count: stats.accounts ?? 0, path: '/view/chart-of-accounts', color: 'bg-sky-100 text-sky-600' },
    { icon: Building2, label: 'Vendors', count: stats.vendors ?? 0, path: '/view/vendors', color: 'bg-amber-100 text-amber-600' },
    { icon: Users, label: 'Customers', count: stats.customers ?? 0, path: '/view/customers', color: 'bg-emerald-100 text-emerald-600' },
    { icon: UserCircle, label: 'Employees', count: stats.employees ?? 0, path: '/view/employees', color: 'bg-purple-100 text-purple-600' },
    { icon: Briefcase, label: 'Enterprises', count: stats.enterprises ?? 0, path: '/view/enterprises', color: 'bg-rose-100 text-rose-600' },
    { icon: Truck, label: 'Equipment', count: stats.equipment ?? 0, path: '/view/equipment', color: 'bg-indigo-100 text-indigo-600' },
  ];

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">File Maintenance</h1>
        <p className="text-gray-500 mt-1">Browse and maintain your master files: accounts, vendors, customers, employees, enterprises and equipment</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {masterLists.map((item, idx) => {
          const Icon = item.icon;
          return (
            <Card 
              key={idx} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(item.path)}
              data-testid={`view-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${item.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{item.label}</h3>
                      <p className="text-sm text-gray-500">{item.count} records</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card 
        className="bg-gradient-to-r from-purple-50 to-sky-50 border-purple-200 cursor-pointer hover:shadow-md"
        onClick={() => navigate('/view/smart-lookup')}
      >
        <CardContent className="p-4 flex items-center gap-4">
          <Sparkles className="w-8 h-8 text-purple-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">AI Smart Lookup</h3>
            <p className="text-sm text-gray-600">Ask AI to find accounts, transactions, or patterns</p>
          </div>
          <Button variant="outline" className="border-purple-300">
            Try It
          </Button>
        </CardContent>
      </Card>
    </>
  );
};

// QuickBooks-style Chart of Accounts
const RENUMBER_ROLES = ['admin', 'manager', 'accountant'];

export const ChartOfAccounts = ({ backTo = '/view' }) => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const { user } = useAuth();
  const canRenumber = RENUMBER_ROLES.includes(user?.role);
  const [renumber, setRenumber] = useState({ newNumber: '', reason: '' });
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({
    Assets: true,
    Liabilities: true,
    Equity: true,
    Income: true,
    Expenses: true
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [accountTypes, setAccountTypes] = useState({});
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newAccount, setNewAccount] = useState({
    number: '',
    name: '',
    type: '',
    category: '',
    sub_type: '',
    description: '',
    opening_balance: ''
  });

  useEffect(() => {
    fetchAccounts();
    fetchAccountTypes();
  }, [companyId]);

  const fetchAccounts = async () => {
    try {
      const params = `?company_id=${companyId || 'default'}`;
      const res = await fetch(`${API_URL}/api/chart-of-accounts/${params}`);
      if (res.ok) {
        const data = await res.json();
        if (data.length === 0) {
          // First time this client's books have been opened: give them the default chart (each client
          // gets their own copy — see initialize_default_accounts in backend/routes/chart_of_accounts.py).
          const initRes = await fetch(`${API_URL}/api/chart-of-accounts/initialize${params}`, { method: 'POST' });
          if (initRes.ok) {
            const refetch = await fetch(`${API_URL}/api/chart-of-accounts/${params}`);
            if (refetch.ok) { setAccounts(await refetch.json()); return; }
          }
        }
        setAccounts(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chart-of-accounts/types`);
      if (res.ok) {
        const data = await res.json();
        setAccountTypes(data);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchNextAccountNumber = async (category, subType) => {
    try {
      const params = new URLSearchParams({ company_id: companyId || 'default' });
      if (subType) params.append('sub_type', subType);
      const res = await fetch(`${API_URL}/api/chart-of-accounts/next-number/${category}?${params}`);
      if (res.ok) {
        const data = await res.json();
        setNewAccount(prev => ({ ...prev, number: data.next_number }));
      }
    } catch (err) {
      console.error('Error fetching next number:', err);
    }
  };

  const handleCategoryChange = (category) => {
    setNewAccount(prev => ({ ...prev, category, type: '', sub_type: '', number: '' }));
    if (category) {
      fetchNextAccountNumber(category);
    }
  };

  // The "Account Type" dropdown (Bank, Fixed Asset, ...) IS the sub_type that drives auto-numbering
  // (e.g. Fixed Assets get 1500-1749, Current Assets get a different range) — see CATEGORY_NUMBER_RANGES
  // in backend/routes/chart_of_accounts.py.
  const handleTypeChange = (type) => {
    setNewAccount(prev => ({ ...prev, type, sub_type: type }));
    if (newAccount.category && type) {
      fetchNextAccountNumber(newAccount.category, type);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.number || !newAccount.name || !newAccount.type || !newAccount.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/chart-of-accounts/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newAccount,
          opening_balance: parseFloat(newAccount.opening_balance) || 0,
          balance: 0,
          is_active: true,
          company_id: companyId || 'default'
        })
      });

      if (res.ok) {
        toast.success('Account created successfully');
        setShowAddModal(false);
        setNewAccount({ number: '', name: '', type: '', category: '', sub_type: '', description: '', opening_balance: '' });
        fetchAccounts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create account');
      }
    } catch (err) {
      toast.error('Failed to create account');
    }
  };

  const handleUpdateAccount = async () => {
    if (!newAccount.name) {
      toast.error('Account name is required');
      return;
    }

    try {
      const params = new URLSearchParams({ company_id: companyId || 'default' });
      const res = await fetch(`${API_URL}/api/chart-of-accounts/${selectedAccount.number}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newAccount.name,
          description: newAccount.description
        })
      });

      if (res.ok) {
        toast.success('Account updated successfully');
        setShowEditModal(false);
        setSelectedAccount(null);
        setNewAccount({ number: '', name: '', type: '', category: '', sub_type: '', description: '', opening_balance: '' });
        fetchAccounts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update account');
      }
    } catch (err) {
      toast.error('Failed to update account');
    }
  };

  const handleRenumber = async () => {
    const newNumber = renumber.newNumber.trim();
    if (!newNumber) { toast.error('Enter the new account number'); return; }
    if (renumber.reason.trim().length < 3) { toast.error('Please give a reason - it is kept in the audit trail'); return; }
    if (!window.confirm(`Change account ${selectedAccount.number} to ${newNumber}?\n\nEvery check, bill, deposit and journal entry using it moves to the new number, and the change is recorded in the audit trail.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/chart-of-accounts/${encodeURIComponent(selectedAccount.number)}/renumber`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_number: newNumber, reason: renumber.reason.trim(), company_id: companyId || 'default' })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const n = Object.values(data.records_updated || {}).reduce((a, b) => a + b, 0);
        toast.success(`${data.message}${n ? ` (${n} records moved with it)` : ''}`);
        setShowEditModal(false);
        setSelectedAccount(null);
        setRenumber({ newNumber: '', reason: '' });
        fetchAccounts();
      } else {
        toast.error(data.detail || 'Could not change the account number');
      }
    } catch (err) {
      toast.error('Could not change the account number');
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/chart-of-accounts/audit-log/list?company_id=${encodeURIComponent(companyId || 'default')}`, { credentials: 'include' });
      setHistory(res.ok ? await res.json() : []);
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteAccount = async (accountNumber) => {
    if (!window.confirm('Are you sure you want to deactivate this account?')) return;

    try {
      const params = new URLSearchParams({ company_id: companyId || 'default' });
      const res = await fetch(`${API_URL}/api/chart-of-accounts/${accountNumber}?${params}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Account deactivated');
        fetchAccounts();
      } else {
        toast.error('Failed to deactivate account');
      }
    } catch (err) {
      toast.error('Failed to deactivate account');
    }
  };

  const openEditModal = (account) => {
    setSelectedAccount(account);
    setNewAccount({
      number: account.number,
      name: account.name,
      type: account.type,
      category: account.category,
      sub_type: account.sub_type || '',
      description: account.description || ''
    });
    setRenumber({ newNumber: '', reason: '' });
    setShowEditModal(true);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Group accounts by category
  const groupedAccounts = accounts.reduce((acc, account) => {
    const cat = account.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(account);
    return acc;
  }, {});

  const categories = ['Assets', 'Liabilities', 'Equity', 'Income', 'Expenses'];

  const getCategoryColor = (cat) => {
    const colors = {
      Assets: 'bg-teal-600',
      Liabilities: 'bg-red-600',
      Equity: 'bg-violet-600',
      Income: 'bg-blue-600',
      Expenses: 'bg-amber-600'
    };
    return colors[cat] || 'bg-slate-500';
  };

  const getCategoryTotal = (cat) => {
    const catAccounts = groupedAccounts[cat] || [];
    return catAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
  };

  const filteredAccounts = (catAccounts) => {
    if (!searchTerm) return catAccounts;
    return catAccounts.filter(acc => 
      acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.number.includes(searchTerm)
    );
  };

  return (
    <>
      <PageHeader
        title="Chart of Accounts"
        subtitle={`${accounts.length} accounts. A changed number takes its history with it.`}
        tabs={[
          { id: 'all', label: 'All accounts', count: accounts.length },
          ...categories.map((c) => ({ id: c, label: c, count: (groupedAccounts[c] || []).length })),
        ]}
        activeTab={selectedCategory}
        onTab={setSelectedCategory}
        actions={
          <>
            <button type="button" className={headerButtonClass} onClick={() => navigate(backTo)}>Back</button>
            <button type="button" className={headerButtonClass} onClick={openHistory} data-testid="number-history-btn">
              <FileText className="w-4 h-4" /> Number change history
            </button>
            <button type="button" className={headerPrimaryClass} onClick={() => setShowAddModal(true)} data-testid="add-account-btn">
              <Plus className="w-4 h-4" /> New account
            </button>
          </>
        }
      />

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search accounts by name or number..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Account List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading accounts...</div>
      ) : (
        <div className="space-y-2">
          {categories.map(category => {
            const catAccounts = filteredAccounts(groupedAccounts[category] || []);
            if (selectedCategory !== 'all' && selectedCategory !== category) return null;
            if (catAccounts.length === 0 && searchTerm) return null;
            
            return (
              <Card key={category} className="overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-4 py-3 bg-card text-foreground font-semibold hover:bg-slate-50"
                  onClick={() => toggleCategory(category)}
                >
                  <div className="flex items-center gap-3">
                    {expandedCategories[category] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <span className={`w-2.5 h-2.5 rounded-sm ${getCategoryColor(category)}`}></span>
                    <span>{category}</span>
                    <span className="text-sm font-normal text-muted-foreground">({catAccounts.length} accounts)</span>
                  </div>
                  <span className="font-mono">${getCategoryTotal(category).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </button>
                
                {expandedCategories[category] && (
                  <div className="divide-y">
                    {catAccounts.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">No accounts in this category</div>
                    ) : (
                      catAccounts.map(account => (
                        <div 
                          key={account.number} 
                          className={`flex items-center justify-between p-3 hover:bg-gray-50 ${account.parent_number ? 'pl-10 bg-gray-50/50' : ''}`}
                          data-testid={`account-row-${account.number}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-sm text-gray-500 w-16">{account.number}</span>
                            <div>
                              <p className="font-medium text-gray-900">{account.name}</p>
                              <p className="text-xs text-gray-500">{account.type}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`font-mono ${account.balance >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                              ${(account.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </span>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditModal(account)} data-testid={`edit-account-${account.number}`}>
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDeleteAccount(account.number)} data-testid={`delete-account-${account.number}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add New Account</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={newAccount.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  data-testid="account-category-select"
                >
                  <option value="">Select Category...</option>
                  {Object.keys(accountTypes).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Number *</Label>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Auto-generated"
                      value={newAccount.number}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, number: e.target.value }))}
                      className={newAccount.number ? 'bg-emerald-50 border-emerald-300' : ''}
                      data-testid="account-number-input"
                    />
                    {newAccount.category && (
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => fetchNextAccountNumber(newAccount.category)}
                        title="Get next number"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {newAccount.number && (
                    <p className="text-xs text-emerald-600">Auto-generated based on category</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Account Type *</Label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={newAccount.type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    disabled={!newAccount.category}
                    data-testid="account-type-select"
                  >
                    <option value="">Select...</option>
                    {newAccount.category && accountTypes[newAccount.category] && 
                      Object.keys(accountTypes[newAccount.category]).map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))
                    }
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Account Name *</Label>
                <Input
                  placeholder="e.g., Checking Account"
                  value={newAccount.name}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="account-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Beginning Balance</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={newAccount.opening_balance}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, opening_balance: e.target.value }))}
                  data-testid="account-opening-balance-input"
                />
                <p className="text-xs text-gray-500">Optional. Posted as an opening journal entry against Opening Balance Equity, dated today.</p>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="Optional description"
                  value={newAccount.description}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button onClick={handleAddAccount} data-testid="create-account-btn">Create Account</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditModal && selectedAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Edit Account</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setShowEditModal(false); setSelectedAccount(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-lg font-bold text-slate-600">{selectedAccount.number}</span>
                  <span className="text-sm text-slate-500">|</span>
                  <span className="text-sm text-slate-500">{selectedAccount.category} → {selectedAccount.type}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Account Name *</Label>
                <Input 
                  value={newAccount.name}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="edit-account-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  placeholder="Optional description"
                  value={newAccount.description}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowEditModal(false); setSelectedAccount(null); }}>Cancel</Button>
                <Button onClick={handleUpdateAccount} data-testid="update-account-btn">Update Account</Button>
              </div>

              <div className="border-t pt-4 space-y-3">
                <Label>Change account number</Label>
                {canRenumber ? (
                  <>
                    <p className="text-xs text-gray-500">
                      Everything already posted to this account (checks, bills, deposits, journal entries) moves to the new number. The change, who made it and your reason are saved in the audit trail.
                    </p>
                    <Input
                      placeholder="New account number"
                      value={renumber.newNumber}
                      onChange={(e) => setRenumber(prev => ({ ...prev, newNumber: e.target.value }))}
                      data-testid="renumber-new-number"
                    />
                    <Input
                      placeholder="Reason for the change (required)"
                      value={renumber.reason}
                      onChange={(e) => setRenumber(prev => ({ ...prev, reason: e.target.value }))}
                      data-testid="renumber-reason"
                    />
                    <div className="flex justify-end">
                      <Button variant="outline" onClick={handleRenumber} data-testid="renumber-btn">Change number</Button>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">Only an administrator, manager or accountant can change an account number.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Number change history */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Account number change history</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHistory(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-500">No account numbers have been changed for this client.</p>
              ) : (
                <div className="space-y-3" data-testid="number-history-list">
                  {history.map((h) => (
                    <div key={h.audit_id} className="p-3 border rounded-lg text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          <span className="font-mono">{h.details?.old_number}</span> &rarr; <span className="font-mono">{h.details?.new_number}</span>
                          {h.details?.account_name ? ` · ${h.details.account_name}` : ''}
                        </span>
                        <span className="text-xs text-gray-500">{h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}</span>
                      </div>
                      <div className="text-gray-600 mt-1">Reason: {h.details?.reason}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        By {h.user_email || h.user_id} ({h.user_role}) · {Object.values(h.details?.records_updated || {}).reduce((a, b) => a + b, 0)} records moved
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Vendors List - Fully Functional
const VendorsList = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    phone: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    industry_code: '',
    naics: '',
    sic: '',
    payment_terms: 'Net 30',
    default_gl_account: '2000',
    tax_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchVendors();
  }, [companyId]);

  const fetchVendors = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/transactions/vendors${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVendors(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async () => {
    if (!formData.name) {
      toast.error('Vendor name is required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/transactions/vendors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, company_id: companyId })
      });

      if (res.ok) {
        toast.success('Vendor created successfully');
        setShowAddModal(false);
        resetForm();
        fetchVendors();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create vendor');
      }
    } catch (err) {
      toast.error('Failed to create vendor');
    }
  };

  const handleUpdateVendor = async () => {
    if (!formData.name) {
      toast.error('Vendor name is required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/transactions/vendors/${selectedVendor.vendor_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Vendor updated successfully');
        setShowEditModal(false);
        resetForm();
        fetchVendors();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update vendor');
      }
    } catch (err) {
      toast.error('Failed to update vendor');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/transactions/vendors/${vendorId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Vendor deleted');
        fetchVendors();
      } else {
        toast.error('Failed to delete vendor');
      }
    } catch (err) {
      toast.error('Failed to delete vendor');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact: '',
      email: '',
      phone: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      industry_code: '',
      naics: '',
      sic: '',
      payment_terms: 'Net 30',
      default_gl_account: '2000',
      tax_id: '',
      notes: ''
    });
    setSelectedVendor(null);
  };

  const openEditModal = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      name: vendor.name || '',
      contact: vendor.contact || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      address2: vendor.address2 || '',
      city: vendor.city || '',
      state: vendor.state || '',
      postal_code: vendor.postal_code || '',
      country: vendor.country || '',
      industry_code: vendor.industry_code || '',
      naics: vendor.naics || '',
      sic: vendor.sic || '',
      payment_terms: vendor.payment_terms || 'Net 30',
      default_gl_account: vendor.default_gl_account || '2000',
      tax_id: vendor.tax_id || '',
      notes: vendor.notes || ''
    });
    setShowEditModal(true);
  };

  const openViewModal = (vendor) => {
    setSelectedVendor(vendor);
    setShowViewModal(true);
  };

  const filteredVendors = vendors.filter(v => 
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.vendor_id?.toLowerCase().includes(searchTerm.toLowerCase()) || v.vendor_number?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="text-gray-500 mt-1">Manage your supplier and vendor accounts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/view')}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
          </Button>
          <Button onClick={() => { resetForm(); setShowAddModal(true); }} data-testid="add-vendor-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Vendor
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search vendors by name, email, or ID..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="vendor-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading vendors...</div>
      ) : filteredVendors.length === 0 ? (
        <Card className="text-center py-12">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">{searchTerm ? 'No vendors found' : 'No vendors yet'}</h3>
          <p className="text-sm text-gray-500 mt-1">{searchTerm ? 'Try a different search term' : 'Add your first vendor to get started'}</p>
          {!searchTerm && (
            <Button className="mt-4" onClick={() => { resetForm(); setShowAddModal(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Add First Vendor
            </Button>
          )}
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Vendor ID</th>
                  <th className="text-left p-3 font-medium text-gray-600">Name</th>
                  <th className="text-left p-3 font-medium text-gray-600">Contact</th>
                  <th className="text-left p-3 font-medium text-gray-600">Phone</th>
                  <th className="text-left p-3 font-medium text-gray-600">Email</th>
                  <th className="text-left p-3 font-medium text-gray-600">Terms</th>
                  <th className="text-right p-3 font-medium text-gray-600">Balance</th>
                  <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredVendors.map(vendor => (
                  <tr key={vendor.vendor_id} className="hover:bg-gray-50" data-testid={`vendor-row-${vendor.vendor_id}`}>
                    <td className="p-3 font-mono text-sm text-sky-600">{vendor.vendor_number || vendor.vendor_id}</td>
                    <td className="p-3 font-medium">{vendor.name}</td>
                    <td className="p-3 text-gray-600">{vendor.contact || '-'}</td>
                    <td className="p-3 text-gray-600">{vendor.phone || '-'}</td>
                    <td className="p-3 text-gray-600">{vendor.email || '-'}</td>
                    <td className="p-3">
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">{vendor.payment_terms}</span>
                    </td>
                    <td className="p-3 text-right font-mono">${(vendor.balance || 0).toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openViewModal(vendor)} data-testid={`view-vendor-${vendor.vendor_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditModal(vendor)} data-testid={`edit-vendor-${vendor.vendor_id}`}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDeleteVendor(vendor.vendor_id)} data-testid={`delete-vendor-${vendor.vendor_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Vendor Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
              <CardTitle>{showAddModal ? 'Add New Vendor' : 'Edit Vendor'}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Vendor Name *</Label>
                  <Input 
                    placeholder="Company Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="vendor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input 
                    placeholder="John Doe"
                    value={formData.contact}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    placeholder="555-123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    placeholder="vendor@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <PartyFields formData={formData} setFormData={setFormData} postalField="postal_code" />
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                  >
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Tax ID</Label>
                  <Input 
                    placeholder="XX-XXXXXXX"
                    value={formData.tax_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, tax_id: e.target.value }))}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notes</Label>
                  <textarea 
                    className="w-full border rounded-md p-2 min-h-[80px]"
                    placeholder="Additional notes about this vendor..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>Cancel</Button>
                <Button onClick={showAddModal ? handleCreateVendor : handleUpdateVendor} data-testid="save-vendor-btn">
                  {showAddModal ? 'Create Vendor' : 'Update Vendor'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Vendor Modal */}
      {showViewModal && selectedVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <CardTitle>Vendor Details</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setShowViewModal(false); setSelectedVendor(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-amber-100 rounded-lg">
                  <Building2 className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedVendor.name}</h3>
                  <p className="text-sm text-gray-500 font-mono">{selectedVendor.vendor_number || selectedVendor.vendor_id}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Contact</p>
                  <p className="font-medium">{selectedVendor.contact || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Phone</p>
                  <p className="font-medium">{selectedVendor.phone || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <p className="font-medium">{selectedVendor.email || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 uppercase">Address</p>
                  <p className="font-medium">{selectedVendor.address || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Payment Terms</p>
                  <p className="font-medium">{selectedVendor.payment_terms}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Balance</p>
                  <p className="font-medium font-mono">${(selectedVendor.balance || 0).toLocaleString()}</p>
                </div>
                {selectedVendor.tax_id && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Tax ID</p>
                    <p className="font-medium">{selectedVendor.tax_id}</p>
                  </div>
                )}
                {selectedVendor.notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 uppercase">Notes</p>
                    <p className="font-medium">{selectedVendor.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowViewModal(false); openEditModal(selectedVendor); }}>
                  <Edit3 className="w-4 h-4 mr-2" /> Edit
                </Button>
                <Button onClick={() => { setShowViewModal(false); setSelectedVendor(null); }}>Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Customers List - Fully Functional
const CustomersList = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    phone: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    industry_code: '',
    naics: '',
    sic: '',
    is_cod: false,
    payment_terms: 'Net 30',
    credit_limit: 0,
    tax_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, [companyId]);

  const fetchCustomers = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/customers${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!formData.name) {
      toast.error('Customer name is required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, company_id: companyId })
      });

      if (res.ok) {
        toast.success('Customer created successfully');
        setShowAddModal(false);
        resetForm();
        fetchCustomers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create customer');
      }
    } catch (err) {
      toast.error('Failed to create customer');
    }
  };

  const handleUpdateCustomer = async () => {
    if (!formData.name) {
      toast.error('Customer name is required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/customers/${selectedCustomer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Customer updated successfully');
        setShowEditModal(false);
        resetForm();
        fetchCustomers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update customer');
      }
    } catch (err) {
      toast.error('Failed to update customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/customers/${customerId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Customer deleted');
        fetchCustomers();
      } else {
        toast.error('Failed to delete customer');
      }
    } catch (err) {
      toast.error('Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact: '',
      email: '',
      phone: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      industry_code: '',
      naics: '',
      sic: '',
      is_cod: false,
      payment_terms: 'Net 30',
      credit_limit: 0,
      tax_id: '',
      notes: ''
    });
    setSelectedCustomer(null);
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name || '',
      contact: customer.contact || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      address2: customer.address2 || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      country: customer.country || '',
      industry_code: customer.industry_code || '',
      naics: customer.naics || '',
      sic: customer.sic || '',
      is_cod: !!customer.is_cod,
      payment_terms: customer.payment_terms || 'Net 30',
      credit_limit: customer.credit_limit || 0,
      tax_id: customer.tax_id || '',
      notes: customer.notes || ''
    });
    setShowEditModal(true);
  };

  const openViewModal = (customer) => {
    setSelectedCustomer(customer);
    setShowViewModal(true);
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.customer_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-500 mt-1">Manage your customer accounts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/view')}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
          </Button>
          <Button onClick={() => { resetForm(); setShowAddModal(true); }} data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Customer
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search customers by name, email, or ID..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="customer-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading customers...</div>
      ) : filteredCustomers.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">{searchTerm ? 'No customers found' : 'No customers yet'}</h3>
          <p className="text-sm text-gray-500 mt-1">{searchTerm ? 'Try a different search term' : 'Add your first customer to get started'}</p>
          {!searchTerm && (
            <Button className="mt-4" onClick={() => { resetForm(); setShowAddModal(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Add First Customer
            </Button>
          )}
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Customer ID</th>
                  <th className="text-left p-3 font-medium text-gray-600">Name</th>
                  <th className="text-left p-3 font-medium text-gray-600">Contact</th>
                  <th className="text-left p-3 font-medium text-gray-600">Phone</th>
                  <th className="text-left p-3 font-medium text-gray-600">Email</th>
                  <th className="text-right p-3 font-medium text-gray-600">Balance</th>
                  <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredCustomers.map(customer => (
                  <tr key={customer.id} className="hover:bg-gray-50" data-testid={`customer-row-${customer.id}`}>
                    <td className="p-3 font-mono text-sm text-emerald-600" title={customer.id}>{customer.customer_number || customer.id}</td>
                    <td className="p-3 font-medium">{customer.name}{customer.is_cod && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold" data-testid="cod-badge">COD</span>}</td>
                    <td className="p-3 text-gray-600">{customer.contact || '-'}</td>
                    <td className="p-3 text-gray-600">{customer.phone || '-'}</td>
                    <td className="p-3 text-gray-600">{customer.email || '-'}</td>
                    <td className="p-3 text-right font-mono">${(customer.open_balance || 0).toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openViewModal(customer)} data-testid={`view-customer-${customer.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditModal(customer)} data-testid={`edit-customer-${customer.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDeleteCustomer(customer.id)} data-testid={`delete-customer-${customer.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Customer Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
              <CardTitle>{showAddModal ? 'Add New Customer' : 'Edit Customer'}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Customer Name *</Label>
                  <Input 
                    placeholder="Company or Individual Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input 
                    placeholder="John Doe"
                    value={formData.contact}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    placeholder="555-123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    placeholder="customer@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <PartyFields formData={formData} setFormData={setFormData} postalField="zip_code" cod />
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                  >
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Credit Limit</Label>
                  <Input 
                    type="number"
                    placeholder="0"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, credit_limit: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notes</Label>
                  <textarea 
                    className="w-full border rounded-md p-2 min-h-[80px]"
                    placeholder="Additional notes about this customer..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>Cancel</Button>
                <Button onClick={showAddModal ? handleCreateCustomer : handleUpdateCustomer} data-testid="save-customer-btn">
                  {showAddModal ? 'Create Customer' : 'Update Customer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Customer Modal */}
      {showViewModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <CardTitle>Customer Details</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setShowViewModal(false); setSelectedCustomer(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-emerald-100 rounded-lg">
                  <Users className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedCustomer.name}</h3>
                  <p className="text-sm text-gray-500 font-mono">{selectedCustomer.id}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Contact</p>
                  <p className="font-medium">{selectedCustomer.contact || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Phone</p>
                  <p className="font-medium">{selectedCustomer.phone || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <p className="font-medium">{selectedCustomer.email || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 uppercase">Address</p>
                  <p className="font-medium">{selectedCustomer.address || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Payment Terms</p>
                  <p className="font-medium">{selectedCustomer.payment_terms || 'Net 30'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Open Balance</p>
                  <p className="font-medium font-mono">${(selectedCustomer.open_balance || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Credit Limit</p>
                  <p className="font-medium font-mono">${(selectedCustomer.credit_limit || 0).toLocaleString()}</p>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowViewModal(false); openEditModal(selectedCustomer); }}>
                  <Edit3 className="w-4 h-4 mr-2" /> Edit
                </Button>
                <Button onClick={() => { setShowViewModal(false); setSelectedCustomer(null); }}>Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Employees List - will show payroll tax info
const EmployeesList = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployees();
  }, [companyId]);

  const fetchEmployees = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/payroll-full/employees${params}`);
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-gray-500 mt-1">Manage employee records and payroll settings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/view')}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
          </Button>
          <Button onClick={() => navigate('/setup/employees')}><Plus className="w-4 h-4 mr-2" /> Add Employee</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading employees...</div>
      ) : employees.length === 0 ? (
        <Card className="text-center py-12">
          <UserCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">No employees yet</h3>
          <p className="text-sm text-gray-500 mt-1">Add employees in Setup → Employees</p>
          <Button className="mt-4" onClick={() => navigate('/setup/employees')}>
            <Plus className="w-4 h-4 mr-2" /> Add First Employee
          </Button>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">ID</th>
                  <th className="text-left p-3 font-medium text-gray-600">Name</th>
                  <th className="text-left p-3 font-medium text-gray-600">Position</th>
                  <th className="text-left p-3 font-medium text-gray-600">Pay Type</th>
                  <th className="text-right p-3 font-medium text-gray-600">Pay Rate</th>
                  <th className="text-center p-3 font-medium text-gray-600">Location</th>
                  <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {employees.map(emp => (
                  <tr key={emp.employee_id} className="hover:bg-gray-50">
                    <td className="p-3 font-mono text-sm">{emp.employee_id}</td>
                    <td className="p-3 font-medium">{emp.name}</td>
                    <td className="p-3 text-gray-600">{emp.position}</td>
                    <td className="p-3">{emp.pay_type === 'salary' ? 'Salary' : 'Hourly'}</td>
                    <td className="p-3 text-right font-mono">
                      ${emp.pay_rate.toLocaleString()}{emp.pay_type === 'hourly' ? '/hr' : '/yr'}
                    </td>
                    <td className="p-3 text-center">
                      {emp.country === 'US' ? `US - ${emp.state}` : emp.country}
                    </td>
                    <td className="p-3 text-center">
                      <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm"><Edit3 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// Placeholder components
const EnterprisesList = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '', type: 'operations', description: '', cost_center: '', budget: 0
  });

  useEffect(() => {
    fetchEnterprises();
  }, [companyId]);

  const fetchEnterprises = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/transactions/enterprises${params}`);
      if (res.ok) setEnterprises(await res.json());
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transactions/enterprises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, company_id: companyId })
      });
      if (res.ok) {
        toast.success('Enterprise created');
        setShowAddModal(false);
        fetchEnterprises();
        setFormData({ name: '', type: 'operations', description: '', cost_center: '', budget: 0 });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to create');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this enterprise?')) return;
    try {
      await fetch(`${API_URL}/api/transactions/enterprises/${id}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchEnterprises();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>;

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Enterprises</h1>
          <p className="text-gray-500 mt-1">Cost accounting by enterprise, operations, and programs</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Enterprise
          </Button>
          <Button variant="outline" onClick={() => navigate('/view')}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
          </Button>
        </div>
      </div>

      {enterprises.length === 0 ? (
        <Card className="text-center py-12">
          <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">No Enterprises Yet</h3>
          <p className="text-sm text-gray-500 mt-1">Create your first enterprise to start cost tracking</p>
          <Button className="mt-4" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Enterprise
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {enterprises.map((ent) => (
            <Card key={ent.enterprise_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${ent.type === 'operations' ? 'bg-sky-100' : ent.type === 'programs' ? 'bg-purple-100' : 'bg-amber-100'}`}>
                      <Briefcase className={`w-5 h-5 ${ent.type === 'operations' ? 'text-sky-600' : ent.type === 'programs' ? 'text-purple-600' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{ent.name}</CardTitle>
                      <span className="text-xs text-gray-500 uppercase">{ent.type}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(ent.enterprise_id)} className="text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Code:</span>
                    <span className="font-medium">{ent.code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Budget:</span>
                    <span className="font-medium">${(ent.budget || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Costs:</span>
                    <span className="font-medium text-red-600">${(ent.total_costs || 0).toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Add Enterprise</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Operations" />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <select className="w-full border rounded-md p-2" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}>
                  <option value="operations">Operations</option>
                  <option value="programs">Programs</option>
                  <option value="projects">Projects</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Cost Center</label>
                <Input value={formData.cost_center} onChange={(e) => setFormData({...formData, cost_center: e.target.value})} placeholder="e.g., CC-100" />
              </div>
              <div>
                <label className="text-sm font-medium">Budget</label>
                <Input type="number" value={formData.budget} onChange={(e) => setFormData({...formData, budget: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleCreate}>Create</Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

const EquipmentList = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState(null);       // the equipment being edited, or null when adding
  const [detail, setDetail] = useState(null);         // the equipment whose costs are on screen
  const BLANK = { equipment_id: '', name: '', category: 'Vehicle', make: '', model: '', year: '', serial_number: '', purchase_date: '', purchase_price: 0, current_value: 0, status: 'active', enterprise: '' };
  const [formData, setFormData] = useState(BLANK);

  const openAdd = () => { setEditing(null); setFormData(BLANK); setShowAddModal(true); };
  const openEdit = (eq) => {
    setEditing(eq);
    setFormData({ ...BLANK, ...Object.fromEntries(Object.entries(eq).filter(([, v]) => v != null)), year: eq.year ?? '' });
    setShowAddModal(true);
  };
  const openDetail = async (eq) => {
    setDetail({ ...eq, transactions: null });
    try {
      const res = await fetch(`${API_URL}/api/transactions/equipment/${encodeURIComponent(eq.equipment_id)}?company_id=${encodeURIComponent(companyId || '')}`);
      if (res.ok) setDetail(await res.json());
    } catch (err) { toast.error('Could not load the costs for this equipment'); }
  };

  useEffect(() => {
    fetchEquipment();
  }, [companyId]);

  const fetchEquipment = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/transactions/equipment${params}`);
      if (res.ok) setEquipment(await res.json());
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) { toast.error('Enter a name for the equipment'); return; }
    try {
      const { total_costs, transactions, ...fields } = formData;
      const res = await fetch(
        editing ? `${API_URL}/api/transactions/equipment/${encodeURIComponent(editing.equipment_id)}?company_id=${encodeURIComponent(companyId || '')}` : `${API_URL}/api/transactions/equipment`,
        {
          method: editing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          // an empty '' year would otherwise be sent as a string where the backend expects an integer (or nothing)
          body: JSON.stringify({ ...fields, year: fields.year === '' ? undefined : parseInt(fields.year, 10), equipment_id: fields.equipment_id || undefined, company_id: companyId })
        }
      );
      if (res.ok) {
        toast.success(editing ? 'Equipment saved' : 'Equipment added');
        setShowAddModal(false);
        setEditing(null);
        fetchEquipment();
        setFormData(BLANK);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to create');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this equipment?')) return;
    try {
      await fetch(`${API_URL}/api/transactions/equipment/${encodeURIComponent(id)}?company_id=${encodeURIComponent(companyId || '')}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchEquipment();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>;

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Equipment</h1>
          <p className="text-gray-500 mt-1">Track equipment, assets, and their costs</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openAdd}>
            <Plus className="w-4 h-4 mr-1" /> Add Equipment
          </Button>
          <Button variant="outline" onClick={() => navigate('/view')}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
          </Button>
        </div>
      </div>

      {equipment.length === 0 ? (
        <Card className="text-center py-12">
          <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">No Equipment Yet</h3>
          <p className="text-sm text-gray-500 mt-1">Add equipment to track costs per asset</p>
          <Button className="mt-4" onClick={openAdd}>
            <Plus className="w-4 h-4 mr-1" /> Add Equipment
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {equipment.map((eq) => (
            <Card key={eq.equipment_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${eq.status === 'active' ? 'bg-emerald-100' : eq.status === 'maintenance' ? 'bg-amber-100' : 'bg-gray-100'}`}>
                      <Truck className={`w-5 h-5 ${eq.status === 'active' ? 'text-emerald-600' : eq.status === 'maintenance' ? 'text-amber-600' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{eq.name}</CardTitle>
                      <span className="text-xs text-gray-500">{eq.category}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openDetail(eq)} className="text-gray-400 hover:text-sky-600" aria-label={`View costs for ${eq.name}`} data-testid={`view-equipment-${eq.equipment_id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(eq)} className="text-gray-400 hover:text-sky-600" aria-label={`Edit ${eq.name}`} data-testid={`edit-equipment-${eq.equipment_id}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(eq.equipment_id)} className="text-gray-400 hover:text-red-500" aria-label={`Delete ${eq.name}`} data-testid={`delete-equipment-${eq.equipment_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Number:</span>
                    <span className="font-mono text-xs">{eq.equipment_id}</span>
                  </div>
                  {eq.make && <div className="flex justify-between">
                    <span className="text-gray-500">Make/Model:</span>
                    <span>{eq.make} {eq.model}</span>
                  </div>}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Purchase Value:</span>
                    <span className="font-medium">${(eq.purchase_price || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Costs:</span>
                    <span className="font-medium text-red-600">${(eq.total_costs || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${eq.status === 'active' ? 'bg-emerald-100 text-emerald-700' : eq.status === 'maintenance' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>
                      {eq.status}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editing ? `Edit ${editing.name}` : 'Add Equipment'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Equipment number</label>
                <Input value={formData.equipment_id} disabled={!!editing} onChange={(e) => setFormData({...formData, equipment_id: e.target.value})} placeholder="Optional. Leave blank to number it automatically, or type your own (e.g., 045)" data-testid="equipment-number-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Truck #01" />
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <select className="w-full border rounded-md p-2" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                  <option value="Vehicle">Vehicle</option>
                  <option value="Machinery">Machinery</option>
                  <option value="Tool">Tool</option>
                  <option value="Computer">Computer</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm font-medium">Make</label>
                  <Input value={formData.make} onChange={(e) => setFormData({...formData, make: e.target.value})} placeholder="e.g., Ford" />
                </div>
                <div>
                  <label className="text-sm font-medium">Model</label>
                  <Input value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} placeholder="e.g., F-150" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Year</label>
                <Input type="number" value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} placeholder="e.g., 2020" />
              </div>
              <div>
                <label className="text-sm font-medium">Serial Number</label>
                <Input value={formData.serial_number} onChange={(e) => setFormData({...formData, serial_number: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Purchase Date</label>
                <Input type="date" value={formData.purchase_date || ''} onChange={(e) => setFormData({...formData, purchase_date: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Enterprise</label>
                <Input value={formData.enterprise || ''} onChange={(e) => setFormData({...formData, enterprise: e.target.value})} placeholder="Optional" />
              </div>
              <div>
                <label className="text-sm font-medium">Purchase Price</label>
                <Input type="number" value={formData.purchase_price} onChange={(e) => setFormData({...formData, purchase_price: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <select className="w-full border rounded-md p-2" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                  <option value="active">Active</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => { setShowAddModal(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleCreate}>{editing ? 'Save' : 'Create'}</Button>
            </div>
          </Card>
        </div>
      )}
      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="equipment-detail">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{detail.name}</CardTitle>
                <p className="text-sm text-gray-500 font-mono">{detail.equipment_id} · {detail.category}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDetail(null)} aria-label="Close"><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div><p className="text-gray-500">Purchase price</p><p className="font-medium">${(detail.purchase_price || 0).toLocaleString()}</p></div>
                <div><p className="text-gray-500">Total costs</p><p className="font-medium text-red-600" data-testid="equipment-total-costs">${(detail.total_costs || 0).toLocaleString()}</p></div>
                <div><p className="text-gray-500">Status</p><p className="font-medium capitalize">{detail.status}</p></div>
              </div>
              <h4 className="font-semibold pt-2">Costs charged to this equipment</h4>
              {detail.transactions === null ? (
                <p className="text-gray-500">Loading...</p>
              ) : detail.transactions?.length ? (
                <table className="w-full">
                  <thead><tr className="text-left text-gray-500 border-b"><th className="py-1">Date</th><th>Description</th><th className="text-right">Amount</th></tr></thead>
                  <tbody>
                    {detail.transactions.map((t, i) => (
                      <tr key={i} className="border-b"><td className="py-1">{String(t.transaction_date || t.date || '').slice(0, 10)}</td><td>{t.description || t.vendor || t.type || ''}</td><td className="text-right font-mono">${(t.amount || 0).toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500">Nothing has been charged to this equipment yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

const TransactionBrowser = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    account: '', equipment_id: '', enterprise: '', cost_center: '', transaction_type: ''
  });
  const [summary, setSummary] = useState({ count: 0, total_amount: 0 });

  useEffect(() => {
    fetchTransactions();
  }, [companyId]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      let params = companyId ? `company_id=${companyId}` : '';
      if (filters.account) params += `&account=${filters.account}`;
      if (filters.equipment_id) params += `&equipment_id=${filters.equipment_id}`;
      if (filters.enterprise) params += `&enterprise=${filters.enterprise}`;
      if (filters.cost_center) params += `&cost_center=${filters.cost_center}`;
      if (filters.transaction_type) params += `&transaction_type=${filters.transaction_type}`;
      
      const res = await fetch(`${API_URL}/api/transactions/browser?${params}`);
      if (res.ok) {
        const data = await res.json();
        setTransactions(data.transactions);
        setSummary({ count: data.count, total_amount: data.total_amount });
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchTransactions();
  };

  const clearFilters = () => {
    setFilters({ account: '', equipment_id: '', enterprise: '', cost_center: '', transaction_type: '' });
    setTimeout(fetchTransactions, 100);
  };

  const getTypeColor = (type) => {
    const colors = {
      check: 'bg-red-100 text-red-700',
      deposit: 'bg-green-100 text-green-700',
      journal_entry: 'bg-purple-100 text-purple-700',
      bill: 'bg-amber-100 text-amber-700',
      invoice: 'bg-sky-100 text-sky-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transaction Browser</h1>
          <p className="text-gray-500 mt-1">Multi-dimensional view of all transactions</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/view')}>
          <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filter by Cost Dimensions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-gray-500">Account</label>
              <Input value={filters.account} onChange={(e) => setFilters({...filters, account: e.target.value})} placeholder="GL Account" className="h-9" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Equipment</label>
              <Input value={filters.equipment_id} onChange={(e) => setFilters({...filters, equipment_id: e.target.value})} placeholder="Equipment ID" className="h-9" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Enterprise</label>
              <Input value={filters.enterprise} onChange={(e) => setFilters({...filters, enterprise: e.target.value})} placeholder="Enterprise" className="h-9" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Cost Center</label>
              <Input value={filters.cost_center} onChange={(e) => setFilters({...filters, cost_center: e.target.value})} placeholder="Cost Center" className="h-9" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Type</label>
              <select className="w-full border rounded-md p-2 h-9 text-sm" value={filters.transaction_type} onChange={(e) => setFilters({...filters, transaction_type: e.target.value})}>
                <option value="">All Types</option>
                <option value="check">Checks</option>
                <option value="deposit">Deposits</option>
                <option value="journal_entry">Journal Entries</option>
                <option value="bill">Bills</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={clearFilters}>Clear</Button>
            <Button size="sm" onClick={applyFilters}>Apply Filters</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-sky-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-sky-700">Total Transactions</p>
              <p className="text-2xl font-bold text-sky-900">{summary.count}</p>
            </div>
            <FileText className="w-8 h-8 text-sky-400" />
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-700">Total Amount</p>
              <p className="text-2xl font-bold text-emerald-900">${summary.total_amount.toLocaleString()}</p>
            </div>
            <DollarSign className="w-8 h-8 text-emerald-400" />
          </CardContent>
        </Card>
      </div>

      {/* Transaction List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-medium text-gray-600">No Transactions Found</h3>
              <p className="text-sm text-gray-500 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Type</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Description</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Account</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Enterprise</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Equipment</th>
                  <th className="text-right p-3 text-sm font-medium text-gray-600">Amount</th>
                  <th className="text-center p-3 text-sm font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr key={tx.id || idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-sm">{tx.date || '-'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(tx.type)}`}>
                        {tx.type?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{tx.description}</td>
                    <td className="p-3 text-sm font-mono">{tx.account || '-'}</td>
                    <td className="p-3 text-sm">{tx.enterprise || '-'}</td>
                    <td className="p-3 text-sm">{tx.equipment_id || '-'}</td>
                    <td className="p-3 text-sm text-right font-medium">${(tx.amount || 0).toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${tx.status === 'posted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </>
  );
};

const SmartLookup = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);

  const handleSearch = () => {
    if (!query.trim()) return;
    setSearching(true);
    toast.loading('AI is searching...');
    setTimeout(() => {
      toast.dismiss();
      toast.success('Found 3 matching results');
      setSearching(false);
    }, 1500);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Smart Lookup</h1>
          <p className="text-gray-500 mt-1">Search with natural language</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/view')}>
          <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
        </Button>
      </div>

      <Card className="bg-gradient-to-r from-purple-50 to-sky-50">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            <h3 className="font-semibold">Ask anything about your data</h3>
          </div>
          <div className="flex gap-2">
            <Input 
              placeholder="e.g., 'Show me all unpaid invoices over $1000' or 'What did we spend on office supplies last quarter?'"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={searching}>
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-1 text-sm bg-white border rounded-full hover:bg-gray-50" onClick={() => setQuery('Show unpaid invoices')}>
              Unpaid invoices
            </button>
            <button className="px-3 py-1 text-sm bg-white border rounded-full hover:bg-gray-50" onClick={() => setQuery('Top 10 customers by revenue')}>
              Top customers
            </button>
            <button className="px-3 py-1 text-sm bg-white border rounded-full hover:bg-gray-50" onClick={() => setQuery('Expenses over budget')}>
              Over budget expenses
            </button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default ViewPage;
